import { useMemo, useRef, useState } from "react"
import { DateTime } from "luxon"

import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { MonetaryAmount } from "../../../../../currency/currencyTypes"
import { OrganizerType } from "../../../../../../shared/types/OrganizerTypes"
import { OrderLineType } from "../../../../../../shared/components/createOrder/createOrderTypes"
import { ProductStockInfo } from "../../../../admin/products/productsTypes"
import { ProductAvailability } from "../../../../../../shared/types/productAvailabilityTypes"
import { useDateTime } from "../../../../../localization/useDateTime"
import { fetchOrderDetails, MeetingServiceQuery } from "../../../../../nonAdminUser/reports/home/meetingServiceApi"
import {
    MerchantMeetingServiceOrdersQuery,
    PartialOrderLine,
    updatePartialMeetingServiceOrder,
} from "../meetingServiceApi"
import { MeetingServiceOrder, PartialOrderLineType } from "../meetingServiceTypes"

export type ProductionEditLine = Readonly<{
    key: string
    name: string
    count: number
    itemPrice: MonetaryAmount
    productId?: number
    isExtraService: boolean
}>

/** Display-only option id for a product wish (no catalog id); never sent to the backend. */
export const CUSTOM_PRODUCT_OPTION_ID = -1

/** A catalog product option shown in the per-line product autocomplete. */
export type ProductionProductOption = Readonly<{
    productId: number
    name: string
    itemPrice: MonetaryAmount
    stock?: ProductStockInfo
    availability?: ProductAvailability
}>

/** Order lines grouped by their delivery — products in one order can have different delivery times. */
export type ProductionDeliveryGroup = Readonly<{
    deliveryKey: string
    deliveryTime: DateTime
    lines: ProductionEditLine[]
}>

/** A catalog product or extra service picked from the "+ Add" flow; both reference a product by id. */
export type AddProductInput = Readonly<{ productId: number; name: string; price: MonetaryAmount }>

/** A free-text "product wish" with a manually entered price and quantity. */
export type AddProductWishInput = Readonly<{ name: string; price: MonetaryAmount; count: number }>

enum AddedLineKind {
    CATALOG = "CATALOG",
    EXTRA_SERVICE = "EXTRA_SERVICE",
    PRODUCT_WISH = "PRODUCT_WISH",
}

// Lines the user adds while editing, kept separate from the persisted order lines. A CATALOG line starts blank
// (no product) and is filled in via the per-line product picker.
type AddedLine =
    | Readonly<{
          key: string
          kind: AddedLineKind.CATALOG
          productId?: number
          name: string
          itemPrice: MonetaryAmount
          count: number
      }>
    | Readonly<{
          key: string
          kind: AddedLineKind.EXTRA_SERVICE
          productId: number
          name: string
          itemPrice: MonetaryAmount
          count: number
      }>
    | Readonly<{
          key: string
          kind: AddedLineKind.PRODUCT_WISH
          name: string
          itemPrice: MonetaryAmount
          count: number
      }>

/** Product chosen for a line from the catalog autocomplete. */
export type SetLineProductInput = Readonly<{ productId: number; name: string; price: MonetaryAmount }>

const ADDED_KEY_PREFIX = "added-"

/** Product catalog lookup for the inline add flow, available once the order details have loaded. */
export type ProductionAddLookup = Readonly<{
    userId: number
    merchantId: number
    priceListId: number
    deliveryDate: string
    deliveryTime: string
    deliveryType: string
    currencyCode: string
}>

/** Passed to `onSaved` so the confirmation can name the order and its new total. */
export type SavedOrderInfo = Readonly<{
    orderId: number
    total: MonetaryAmount
}>

type UseProductionOrderEditParams = Readonly<{
    order: MeetingServiceOrder
    /** Opens the shared delete-order modal when an edit would leave the order with no products. */
    onRequestDelete: () => void
    /** Called after the partial update succeeds, with the saved order id and its new total. */
    onSaved?: (info: SavedOrderInfo) => void
}>

/**
 * Per-card quick-edit state for the production view: a quantity of 0 removes the line (the trash action
 * is a shortcut to 0), and removing the last remaining product routes to the shared delete-order modal
 * instead of leaving an empty order. Lines are grouped by delivery time. `save` persists the result via the
 * partial meeting-service update endpoint, matched by delivery time.
 */
export function useProductionOrderEdit({ order, onRequestDelete, onSaved }: UseProductionOrderEditParams) {
    const dateTimeFactory = useDateTime()

    const [editingKey, setEditingKey] = useState<string | null>(null)
    const [editingTime, setEditingTime] = useState<DateTime | null>(null)
    const isEditing = editingKey !== null
    const [itemEdits, setItemEdits] = useState<Record<string, number>>({})
    const [removedKeys, setRemovedKeys] = useState<string[]>([])
    const [productEdits, setProductEdits] = useState<Record<string, SetLineProductInput>>({})
    const [addedLines, setAddedLines] = useState<AddedLine[]>([])
    const [saveAttempted, setSaveAttempted] = useState(false)
    // Lines touched by a save attempt (like Formik on submit); lines added afterwards stay untouched.
    const [touchedKeys, setTouchedKeys] = useState<string[]>([])
    const [savingGroups, setSavingGroups] = useState<ProductionDeliveryGroup[] | null>(null)
    const addedKeyRef = useRef(0)

    const zeroPrice: MonetaryAmount = { amount: 0, scale: 2, currency: order.amount.currency }

    // The production-list order omits the owner's user id that the partial-update payload needs, so we load
    // the full order from the details endpoint lazily once the card enters edit mode.
    const detailsQuery = useApiQuery(fetchOrderDetails, {
        queryName: MeetingServiceQuery.ORDER_DETAILS,
        dependencies: { orderId: order.id },
        enabled: isEditing,
        staleTime: 0,
        select: (data) => data.orders[0],
    })
    const detailsOrder = detailsQuery.data
    // The order operations (partial update + product catalog) run as the ON-BEHALF user, i.e. the ORGANIZER,
    // NOT `customer` (which can be the canteen/creator whose id the product endpoint rejects). Fall back to the
    // CREATOR when the organizer id is missing or 0, mirroring the create-order flow.
    const organizers = detailsOrder?.organizers ?? []
    const organizerId = organizers.find((organizer) => organizer.type === OrganizerType.ORGANIZER)?.id
    const creatorId = organizers.find((organizer) => organizer.type === OrganizerType.CREATOR)?.id
    const ownerUserId = (organizerId ? organizerId : undefined) ?? (creatorId ? creatorId : undefined)

    const { mutate: savePartial, isLoading: isSaving } = useApiMutation(updatePartialMeetingServiceOrder, {
        async onSuccess(_params, data, context) {
            // Keep the edit UI (and its progress bar) until the refetch lands, then exit to the fresh data.
            await Promise.all([
                context.queryClient.invalidateQueries({
                    queryKey: [MerchantMeetingServiceOrdersQuery.MEETING_SERVICE],
                }),
                context.queryClient.invalidateQueries({ queryKey: [MeetingServiceQuery.ORDER_DETAILS] }),
            ])
            setEditingKey(null)
            setEditingTime(null)
            resetEdits()
            // The response carries the updated order — use its id and new total for the page-level saved banner.
            if (data.order) onSaved?.({ orderId: data.order.id, total: data.order.amount })
        },
        // Re-throw so the API error still surfaces as the standard snackbar (a custom onError
        // otherwise swallows it).
        async onError(_params, error) {
            setSavingGroups(null)
            throw error
        },
    })

    // Extra-service product ids, recovered from the details order (the list may omit `type` on some lines).
    const extraServiceProductIds = useMemo(() => {
        const ids = new Set<number>()
        for (const delivery of detailsOrder?.deliveries ?? []) {
            for (const orderLine of delivery.orderLines) {
                if (orderLine.type === OrderLineType.EXTRA_SERVICE) ids.add(orderLine.productId)
            }
        }
        return ids
    }, [detailsOrder])

    // Lines are keyed by stable position (deliveryIndex-lineIndex) so edits map back to them.
    const baseGroups = useMemo(
        () =>
            order.deliveries.map((delivery, deliveryIndex) => ({
                deliveryKey: String(deliveryIndex),
                deliveryTime: delivery.deliveryTime,
                lines: delivery.orderLines.map((orderLine, lineIndex) => ({
                    key: `${deliveryIndex}-${lineIndex}`,
                    name: orderLine.name,
                    items: orderLine.items,
                    itemPrice: orderLine.itemPrice,
                    productId: orderLine.productId,
                    isExtraService: orderLine.type === OrderLineType.EXTRA_SERVICE,
                })),
            })),
        [order.deliveries],
    )

    const editedGroups = baseGroups
        .map((group) => ({
            deliveryKey: group.deliveryKey,
            deliveryTime: dateTimeFactory.fromApi(group.deliveryTime),
            lines: group.lines
                .filter((line) => !removedKeys.includes(line.key))
                .map((line) => {
                    // A product swap overrides the line's product id, name and unit price.
                    const swapped = productEdits[line.key]
                    return {
                        key: line.key,
                        name: swapped?.name ?? line.name,
                        count: itemEdits[line.key] ?? line.items,
                        itemPrice: swapped?.price ?? line.itemPrice,
                        productId: swapped?.productId ?? line.productId,
                        isExtraService:
                            line.isExtraService ||
                            (line.productId !== undefined && extraServiceProductIds.has(line.productId)),
                    }
                }),
        }))
        .filter((group) => group.lines.length > 0)

    // Newly added lines belong to the delivery being edited, so they use its exact DateTime to merge into the
    // same section.
    const addedDeliveryTime = editingTime ?? dateTimeFactory.fromApi(order.deliveryDetails.time)
    const addedDisplayGroup: ProductionDeliveryGroup | null =
        addedLines.length > 0
            ? {
                  deliveryKey: "added",
                  deliveryTime: addedDeliveryTime,
                  lines: addedLines.map((line) => ({
                      key: line.key,
                      name: line.name,
                      count: line.count,
                      itemPrice: line.itemPrice,
                      productId: line.kind === AddedLineKind.PRODUCT_WISH ? undefined : line.productId,
                      isExtraService: line.kind === AddedLineKind.EXTRA_SERVICE,
                  })),
              }
            : null

    const deliveryGroups: ProductionDeliveryGroup[] = [
        ...editedGroups.map((group) => ({
            deliveryKey: group.deliveryKey,
            deliveryTime: group.deliveryTime,
            lines: group.lines.map(({ key, name, count, itemPrice, productId, isExtraService }) => ({
                key,
                name,
                count,
                itemPrice,
                productId,
                isExtraService,
            })),
        })),
        ...(addedDisplayGroup ? [addedDisplayGroup] : []),
    ]

    const displayGroups = savingGroups ?? deliveryGroups

    const lines = displayGroups.flatMap((group) => group.lines)

    // Lines of the delivery currently being edited (its own products + any added lines), for the edit total.
    const editingLines = editingKey
        ? displayGroups
              .filter((group) => group.deliveryKey === "added" || group.deliveryTime.toISO() === editingKey)
              .flatMap((group) => group.lines)
        : []

    // A blank added line (product picker never used): no product id and no name; can't be saved
    const hasIncompleteLine = editingLines.some(
        (line) => !line.isExtraService && line.productId === undefined && line.name.trim() === "",
    )

    // Products already used in the edited delivery, so the per-line picker can hide them (no duplicates).
    const usedProductIds = editingLines
        .map((line) => line.productId)
        .filter((productId): productId is number => productId !== undefined)

    const hasChanges =
        removedKeys.length > 0 ||
        Object.keys(itemEdits).length > 0 ||
        Object.keys(productEdits).length > 0 ||
        addedLines.length > 0

    const resetEdits = () => {
        setItemEdits({})
        setRemovedKeys([])
        setProductEdits({})
        setAddedLines([])
        setSaveAttempted(false)
        setTouchedKeys([])
        setSavingGroups(null)
    }

    const startEdit = (deliveryKey: string, deliveryTime: DateTime) => {
        resetEdits()
        setEditingKey(deliveryKey)
        setEditingTime(deliveryTime)
    }

    const cancelEdit = () => {
        setEditingKey(null)
        setEditingTime(null)
        resetEdits()
    }

    const removeLine = (key: string) => {
        // Added lines aren't persisted yet, so removing one just drops it from local state.
        if (key.startsWith(ADDED_KEY_PREFIX)) {
            setAddedLines((prev) => prev.filter((line) => line.key !== key))
            return
        }
        // Removing the final remaining line empties the order, which equals deleting it — route to
        // the shared delete modal and leave the state intact so cancelling the modal restores it.
        if (lines.length <= 1) {
            onRequestDelete()
            return
        }
        setRemovedKeys((prev) => [...prev, key])
        setItemEdits((prev) => {
            const { [key]: _removed, ...rest } = prev
            return rest
        })
    }

    const setLineCount = (key: string, count: number) => {
        if (count <= 0) {
            removeLine(key)
            return
        }
        if (key.startsWith(ADDED_KEY_PREFIX)) {
            setAddedLines((prev) => prev.map((line) => (line.key === key ? { ...line, count } : line)))
            return
        }
        setItemEdits((prev) => ({ ...prev, [key]: count }))
    }

    const nextAddedKey = () => `${ADDED_KEY_PREFIX}${addedKeyRef.current++}`

    // Adds a blank product line; the product is chosen afterwards via the per-line catalog autocomplete.
    const addBlankLine = () =>
        setAddedLines((prev) => [
            ...prev,
            { key: nextAddedKey(), kind: AddedLineKind.CATALOG, name: "", itemPrice: zeroPrice, count: 1 },
        ])

    const setLineProduct = (key: string, product: SetLineProductInput) => {
        // Picking a product turns any added line (blank or a product wish) into a catalog line.
        if (key.startsWith(ADDED_KEY_PREFIX)) {
            setAddedLines((prev) =>
                prev.map((line) =>
                    line.key === key
                        ? {
                              key: line.key,
                              kind: AddedLineKind.CATALOG,
                              productId: product.productId,
                              name: product.name,
                              itemPrice: product.price,
                              count: line.count,
                          }
                        : line,
                ),
            )
            return
        }
        setProductEdits((prev) => ({ ...prev, [key]: product }))
    }

    const addExtraService = ({ productId, name, price }: AddProductInput) =>
        setAddedLines((prev) => [
            ...prev,
            { key: nextAddedKey(), kind: AddedLineKind.EXTRA_SERVICE, productId, name, itemPrice: price, count: 1 },
        ])

    const addProductWish = ({ name, price, count }: AddProductWishInput) =>
        setAddedLines((prev) => [
            ...prev,
            { key: nextAddedKey(), kind: AddedLineKind.PRODUCT_WISH, name, itemPrice: price, count },
        ])

    // Details load determines which lines are non-editable, so keep the whole edit row disabled until ready.
    const isEditLoading = isEditing && detailsQuery.isInitialLoading
    const isSavePending = isSaving || isEditLoading

    const save = () => {
        setSaveAttempted(true)
        setTouchedKeys(editingLines.map((line) => line.key))
        if (!hasChanges || hasIncompleteLine || isSaving || ownerUserId === undefined || !detailsOrder) return
        // Delivery id and buyerParty are intentionally omitted so the partial update keeps the order's existing
        // buyerParty and accounting info untouched.
        const seats = order.deliveryDetails.sittings
        const addedOrderLines: PartialOrderLine[] = addedLines.flatMap((line): PartialOrderLine[] => {
            switch (line.kind) {
                case AddedLineKind.EXTRA_SERVICE:
                    return [{ type: PartialOrderLineType.EXTRA_SERVICE, productId: line.productId, items: line.count }]
                case AddedLineKind.PRODUCT_WISH:
                    return [
                        {
                            type: PartialOrderLineType.PRODUCT_WISH,
                            productName: line.name,
                            items: line.count,
                            price: line.itemPrice,
                        },
                    ]
                case AddedLineKind.CATALOG:
                    // A blank line whose product was never picked is dropped.
                    return line.productId !== undefined ? [{ productId: line.productId, items: line.count }] : []
            }
        })
        // Extra services round-trip as EXTRA_SERVICE (sent as a plain product, the backend drops them).
        const toExistingPayloadLine = (productId: number, items: number): PartialOrderLine =>
            extraServiceProductIds.has(productId)
                ? { type: PartialOrderLineType.EXTRA_SERVICE, productId, items }
                : { productId, items }

        // The endpoint expects ONE delivery per delivery time with all its lines, so group lines that share a time.
        const deliveriesByTime = new Map<string, { deliveryTime: string; orderLines: PartialOrderLine[] }>()
        const pushLine = (deliveryTime: DateTime, orderLine: PartialOrderLine) => {
            // Strict "yyyy-MM-dd HH:mm": the endpoint rejects the ISO "T" and seconds (500).
            const apiTime = dateTimeFactory.toApi(deliveryTime)
            const existing = deliveriesByTime.get(apiTime)
            if (existing) {
                existing.orderLines.push(orderLine)
            } else {
                deliveriesByTime.set(apiTime, { deliveryTime: apiTime, orderLines: [orderLine] })
            }
        }
        for (const group of editedGroups) {
            for (const line of group.lines) {
                pushLine(group.deliveryTime, toExistingPayloadLine(line.productId, line.count))
            }
        }
        for (const orderLine of addedOrderLines) {
            pushLine(addedDeliveryTime, orderLine)
        }

        const deliveries = Array.from(deliveriesByTime.values(), ({ deliveryTime, orderLines }) => ({
            deliveryTime,
            seats,
            orderLines,
        }))
        setSavingGroups(deliveryGroups)
        savePartial({ userId: ownerUserId, orderId: order.id, deliveries })
    }

    // The add flow needs the user + price list to load the catalog; these come from the details order.
    const addLookup: ProductionAddLookup | undefined =
        ownerUserId !== undefined && detailsOrder?.merchant?.id && detailsOrder?.priceList?.id
            ? {
                  userId: ownerUserId,
                  merchantId: detailsOrder.merchant.id,
                  priceListId: detailsOrder.priceList.id,
                  deliveryDate: addedDeliveryTime.toISODate() ?? "",
                  deliveryTime: dateTimeFactory.toApiISOMinutesFormat(addedDeliveryTime),
                  deliveryType: order.deliveryDetails.type,
                  currencyCode: order.amount.currency,
              }
            : undefined

    return {
        isEditing,
        editingKey,
        editingLines,
        deliveryGroups: displayGroups,
        hasChanges,
        hasIncompleteLine,
        usedProductIds,
        saveAttempted,
        touchedKeys,
        isSaving: isSavePending,
        isEditLoading,
        addLookup,
        startEdit,
        cancelEdit,
        setLineCount,
        setLineProduct,
        removeLine,
        addBlankLine,
        addExtraService,
        addProductWish,
        save,
    }
}

export type ProductionOrderEdit = ReturnType<typeof useProductionOrderEdit>
